/* eslint-disable react/no-is-mounted */

import { computed, makeAutoObservable, makeObservable, observable, runInAction } from "mobx";
import { distance } from "fastest-levenshtein";
import validator from "validator";

import { Project } from "project";

import client, { ApiError, ReportStatuses } from "client";
import { getCookie } from "utils/cookie";

import messageBoxStore from "MessageBox/MessageBoxStore";
import popupStore from "components/Popup/PopupStore";

import MarketStore from "pages/markets/MarketStore";

// import AppleMusic from "markets/AppleMusic";
import AptekaRu from "markets/AptekaRu";
import Avito from 'markets/Avito';
import GoogleSeo from "markets/GoogleSeo";
import Megamarket from 'markets/Megamarket';
import Ozon from 'markets/Ozon';
// import Spotify from "markets/Spotify";
import Telegram from 'markets/Telegram';
import Vk from 'markets/Vk';
import VkMusic from "markets/VkMusic";
import Wildberries from 'markets/Wildberries';
import YandexMaps from 'markets/YandexMaps';
import YandexMarket from 'markets/YandexMarket';
import YandexMusic from "markets/YandexMusic";
import YandexSeo from "markets/YandexSeo";

const WARNING_MESSAGE = [
  "Необходимо проверить отчет исполнителя.",
  <br />,
  <br />,
  "Если ответы на контрольные вопросы верны, то примите задание. " +
    "Исполнитель получит оплату за свою работу.",
  <br />,
  <br />,
  "Если ответы отсутствуют или неверные, то отклоните или отправьте на доработку.",
];

/**
 * Список всех площадок, в порядке их представления в интерфейсе.
 */
export const MARKETS = [
  Avito,
  YandexMaps,
  YandexSeo,
  GoogleSeo,
  Ozon,
  Wildberries,
  Telegram,
  Vk,
  YandexMusic,
  VkMusic,
  // Spotify,
  // AppleMusic,
  YandexMarket,
  Megamarket,
  AptekaRu,
];

/**
 * Список всех групп площадок, в порядке их представления в интерфейсе.
 */
export const MARKET_GROUPS = [
  {
    title: 'Маркетплейсы и торговля',
    markets: [
      Avito,
      Ozon,
      Wildberries,
      YandexMarket,
      Megamarket,
      AptekaRu,
    ]
  },
  {
    title: 'Мессенджеры и социальные сети',
    markets: [
      Telegram,
      Vk,
    ]
  },
  {
    title: 'SEO и поисковики',
    markets: [
      YandexSeo,
      GoogleSeo,
    ]
  },
  {
    title: 'Агрегаторы и карты',
    markets: [
      YandexMaps,
    ]
  },
  {
    title: 'Музыка и подкасты',
    markets: [
      YandexMusic,
      VkMusic,
      // Spotify,
      // AppleMusic,
    ]
  }
];

export const ENABLED_MARKETS = MARKETS.filter(market => !market.disabled);

export const ENABLED_MUSIC_PLATFORMS = ENABLED_MARKETS.filter(
  market => market.isMusicPlatform
);

export const ENABLED_MARKET_GROUPS = MARKET_GROUPS.map(marketGroup => {
  const enabledMarketGroup = { ...marketGroup };
  enabledMarketGroup.markets = enabledMarketGroup.markets.filter(
    market => !market.disabled
  );
  return enabledMarketGroup;
}).filter(marketGroup => marketGroup.markets.length > 0);

const MAX_SCREENSHOT_SIZE_PX = 1024;

class AppStoreClass {
  constructor() {
    makeAutoObservable(this);

    this.executorType = "humans";
    this.savedHidePaymentNotification();
    this.loadHasSeenFirstProjectBotNotification();
  }

    // строка уведомлений
    notification = null;
    setNotification(notificationData) {
      this.notification = notificationData;
    }
    clearNotification() {
      this.notification = null;
    }

  isMarketPage(pathname) {
    return !!this.findMarketStoreAndCalculatorByPath(pathname);
  }

  token = getCookie("jwt");

  setToken(token) {
    this.token = token;
    if (token) document.cookie = `jwt=${token};path=/;max-age=31536000`;
    else document.cookie = "jwt=;path=/;expires=-1";
  }

  get isAuth() {
    return !!this.token;
  }

  isFormModeLogin = true;
  setIsFormModeLogin(mode) {
    this.isFormModeLogin = mode;
  }

  warningVisible = false;
  setWarningVisible(visible) {
    this.warningVisible = visible;
  }

  // TODO: use profile member?
  userId = undefined;
  userEmail = undefined;
  telegramNotify = undefined;
  telegramId = undefined;
  sponsorId = undefined;

  profile = undefined;
  updatingProfile = false;
  async updateProfile() {
    if (this.updatingProfile) return false;

    this.updatingProfile = true;
    try {
      this.profile = await client.getProfile();
      runInAction(() => {
        // TODO: use this.profile?
        this.userId = this.profile.id;
        this.userEmail = this.profile.login;
        this.telegramNotify = this.profile.telegramNotify;
        this.telegramId = this.profile.telegramId ?? null;
        this.sponsorId = this.profile.sponsorId ?? null;
      });
      return true;
    } finally {
      this.updatingProfile = false;
    }
  }

  async updateProfileAndMaybeShowWarning() {
    this.setWarningVisible(
      (await this.updateProfile()) && this.profile.warning
    );
  }

  OTP = null;
  receivingCode = false;
  async turnOnNotifications() {
    if (this.receivingCode) return false;

    this.receivingCode = true;
    try {
      const response = await client.issueCode();
      runInAction(() => {
        this.OTP = response.text;
      });
      return true;
    } catch (error) {
      console.error("Ошибка при изменении статуса уведомлений:", error);
      throw error;
    } finally {
      this.receivingCode = false;
    }
  }

  async onOffTelegramNotifications(notify = true) {
    try {
      const response = await client.onOffTelegramNotifications(notify);
      await this.updateProfile();
      return response.message;
    } catch (error) {
      console.error("Ошибка при изменении статуса уведомлений:", error);
      throw error;
    }
  }

  agreedRules = false;
  agreedPrivacy = false;
  agreedWithRules = false;

  setAgreedRules(value) {
    this.agreedRules = value;
  }

  setAgreedPrivacy(value) {
    this.agreedPrivacy = value;
  }

  setAgreedWithRules(value) {
    this.agreedWithRules = value;
  }

  legalConfirm = false;
  setLegalConfirm(flag) {
    this.legalConfirm = flag;
  }

  adultConfirm = false;
  setAdultConfirm(flag) {
    this.adultConfirm = flag;
  }

  politicalConfirm = false;
  setPoliticalConfirm(flag) {
    this.politicalConfirm = flag;
  }

  resetAgreed() {
    this.agreedRules = false;
    this.agreedPrivacy = false;
    this.legalConfirm = false;
    this.adultConfirm = false;
    this.politicalConfirm = false;
  }

  acceptAgreementsUser = false;
  async updateAcceptAgreements() {
    if (this.acceptAgreementsUser) return false;

    this.acceptAgreementsUser = true;
    try {
      await client.acceptAgreements();
      this.updateProfile();
      return true;
    } finally {
      this.acceptAgreementsUser = false;
    }
  }

  async handleAgreeClickForLoggedInUser() {
    if (this.isAuth) {
      await this.updateAcceptAgreements();
    }
  }

  hidePaymentNotification = false;
  setHidePaymentNotification(value) {
    this.hidePaymentNotification = value;
    localStorage.setItem('hidePaymentNotification', value);
  }

  savedHidePaymentNotification() {
    const savedHidePaymentNotification = localStorage.getItem('hidePaymentNotification');
    if (savedHidePaymentNotification !== null) {
      this.hidePaymentNotification = JSON.parse(savedHidePaymentNotification);
    }
  }

  showFirstProjectBotNotification = false;
  hasSeenFirstProjectBotNotification = false;

  setShowFirstProjectBotNotification(value = true) {
    this.showFirstProjectBotNotification = value;
  }
  
  setHasSeenFirstProjectBotNotification(value = true) {
    this.hasSeenFirstProjectBotNotification = value;
    localStorage.setItem('hasSeenFirstProjectBotNotification', JSON.stringify(value));
  }
  
  loadHasSeenFirstProjectBotNotification() {
    const hasSeenNotification = localStorage.getItem('hasSeenFirstProjectBotNotification');
    if (hasSeenNotification !== null) {
      this.hasSeenFirstProjectBotNotification = JSON.parse(hasSeenNotification);
    }
  }

  marketStores = MARKETS.map(market => new MarketStore(market));
  currentMarketStoreIndex = (() => {
    const path = document.location.pathname;
    const marketStoreIndex = this.marketStores.findIndex((marketStore) =>
      path.includes(marketStore.market.id)
    );
    if (marketStoreIndex >= 0) return marketStoreIndex;
    return 0;
  })();
  currentMarketStore = this.marketStores[this.currentMarketStoreIndex];

  findMarketStoreById(id) {
    return this.marketStores.find(
      (marketStore) => marketStore.market.id === id
    );
  }

  findMarketStoreAndCalculatorByPath(pathname) {
    while (pathname.endsWith('/'))
      pathname = pathname.substring(0, pathname.length - 1);
    if (pathname.endsWith('-new'))
      pathname = pathname.substring(0, pathname.length - '-new'.length);
    const path = pathname ?? '/';

    for (let i = 0; i < this.marketStores.length; ++i) {
      for (let j = 0; j < this.marketStores[i].calculators.length; ++j) {
        if (this.marketStores[i].calculators[j].mode.path === path)
          return { i: i, j: j };
      }
    }
    return null;
  }

  selectMarketStoreByIndex(index) {
    this.currentMarketStoreIndex = index;
    this.currentMarketStore = this.marketStores[index];
  }

  selectMarketStoreAndCalculatorByIndexes(i, j) {
    this.selectMarketStoreByIndex(i);
    this.currentMarketStore.selectCalculator(j);
  }

  selectMarketStoreAndCalculatorByPath(path) {
    const ij = this.findMarketStoreAndCalculatorByPath(path);
    if (!ij) return false;
    this.selectMarketStoreAndCalculatorByIndexes(ij.i, ij.j);
    return true;
  }

  marketsMap = MARKETS.reduce((map, market) => {
    map[market.id] = market;
    return map;
  }, {});

  projects = null;

  removeProjectLocally(project) {
    this.projects = this.projects.filter((p) => p !== project);
  }

  isUpdatingProjects = false;
  isDeletingProject = false;
  isPayingProject = false;
  isFinishingProject = false;
  isRepeatingProject = false;
  isCancellingProject = false;

  async updateProjects({ shouldClearCurrent = false } = {}) {
    if (this.isUpdatingProjects) return;

    if (shouldClearCurrent) this.projects = null;

    this.isUpdatingProjects = true;
    try {
      const projects = (await client.getProjects()).reverse();
      runInAction(() => {
        this.projects = projects.map((project) =>
          makeObservable(
            new Project(project),
            {
              title: observable,
              titleToShow: computed,
              fullTitle: computed,
              link: observable,
              screenshot: observable,
              reports: observable,
            }
          )
        );

        if (!this.telegramId && !this.hasSeenFirstProjectBotNotification && projects.length === 1) {
          this.turnOnNotifications().then(() => {
            this.setShowFirstProjectBotNotification(true);
            popupStore.open("bot-modal");
          }).catch(error => {
            messageBoxStore.showError("Ошибка при получении кода для Telegram-бота");
            console.error("Ошибка при получении кода для Telegram-бота:", error);
          });
        }
      });
    } catch (e) {
      if (e instanceof ApiError)
        messageBoxStore.showError(e.message);
      else
        throw e;
    } finally {
      runInAction(() => {
        this.isUpdatingProjects = false;
      });
    }
  }

  deleteProjectWithConfirm(project) {
    popupStore.setText("Вы действительно хотите удалить проект?");
    popupStore.setOk(() => {
      this.deleteProject(project);
    });
    popupStore.open("suremodal");
  }

  async deleteProject(project) {
    if (this.isDeletingProject) return;

    this.isDeletingProject = true;
    try {
      await client.deleteProject(project.id);
      this.removeProjectLocally(project);
      await this.updateProjects();
    } finally {
      runInAction(() => {
        this.isDeletingProject = false;
      });
    }
  }

  async payProject(project) {
    if (this.isPayingProject) return;

    this.isPayingProject = true;
    try {
      if (!(await client.payProject(project.id, project.price))) {
        return messageBoxStore.showError(
          "Недостаточно средств!",
          null,
          "Пополнить",
          () => {
            popupStore.open("paymodal");
          }
        );
      }

      if (!this.hidePaymentNotification) {
        messageBoxStore.showInfo(
          "Проект успешно оплачен и находится в разделе Активные проекты",
          null,
          "Ок",
          null,
          true
        );
        this.setActiveTab("active");
      }

      this.removeProjectLocally(project);

      await this.updateProfile();
      await this.updateProjects();
    } catch (e) {
      if (e instanceof ApiError)
        messageBoxStore.showError(e.message ?? 'Не удалось запустить проект');
      else
        throw e;
    } finally {
      runInAction(() => {
        this.isPayingProject = false;
      });
    }
  }

  async stopProjectWithConfirm(project) {
    popupStore.setOk(async () => {
      try {
        await this.cancelProject(project);
      } catch (e) {
        if (e instanceof ApiError) {
          messageBoxStore.showError(e.message);
          return;
        }
        throw e;
      }
    });
    popupStore.open("stopmodal");
  }

  async archiveProjectWithConfirm(project) {
    popupStore.setText("Вы действительно хотите архивировать проект?");
    popupStore.setOk(() => {
      this.finishProject(project);
    });
    popupStore.open("suremodal");
  }

  async finishProject(project) {
    if (this.isFinishingProject) return;

    this.isFinishingProject = true;
    try {
      await client.finishProject(project.id);
      this.removeProjectLocally(project);
      await this.updateProjects();
    } finally {
      runInAction(() => {
        this.isFinishingProject = false;
      });
    }
  }

  async cancelProject(project) {
    if (this.isCancellingProject) return;

    this.isCancellingProject = true;
    try {
      await client.cancelProject(project.id);
      this.removeProjectLocally(project);
      await this.updateProjects();
    } finally {
      runInAction(() => {
        this.isCancellingProject = false;
      });
    }
  }

  async repeatProject(projectId, captchaToken) {
    if (this.isRepeatingProject)
      return;

    this.isRepeatingProject = true;
    try {
      await client.copyProject(projectId, captchaToken);
      await this.updateProjects();
      messageBoxStore.showInfo(
        "Повтор проекта успешно выполнен и находится в разделе «Черновики»"
      );
      this.setActiveTab("drafts");
    } finally {
      runInAction(() => {
        this.isRepeatingProject = false;
      });
    }
  }

  setReportStatus(projectId, reportId, status) {
    this.projects
      .find((project) => project.id === projectId)
      .reports.find((report) => report.id === reportId).status = status;
  }

  setReportAsReplaced(projectId, reportId) {
    this.setReportStatus(projectId, reportId, ReportStatuses.REPLACE);
  }
  setReportsAsApproved(projectId, reportId) {
    this.setReportStatus(projectId, reportId, ReportStatuses.APPROVED);
  }
  setReportAsRejected(projectId, reportId) {
    this.setReportStatus(projectId, reportId, ReportStatuses.REWORK);
  }

  // настройки проекта:
  executorType = "humans"; // "humans" или "bots"
  setExecutorType(type) {
    this.executorType = type;
  }

  isSetupOpen = false;
  setIsSetupOpen(value) {
    this.isSetupOpen = value;
  }

  openSetupProjectId = null;
  setOpenSetupProjectId(project) {
    this.openSetupProjectId = project;
  }

  editingTitle = false;
  setEditingTitle(value) {
    this.editingTitle = value;
  }

  newTitle = "";
  setNewTitle(value) {
    this.newTitle = value;
  }

  editingProjectId = null;
  isEditingProjectTitle = false;
  startEditingProjectTitle(projectId) {
    this.isEditingProjectTitle = true;
    this.editingProjectId = projectId;
  }

  stopEditingProjectTitle() {
    this.isEditingProjectTitle = false;
    this.editingProjectId = null;
  }

  isValidProjectTitle(currentTitle, newTitle) {
    if (newTitle === null || newTitle === undefined) return false;
    const trimmedNewTitle = newTitle.trim();
    if (!trimmedNewTitle) return false;
    if (currentTitle === trimmedNewTitle) return false;

    return true;
  }

  newLink = "";
  setNewLink(value) {
    this.newLink = value;
  }

  isEditingNewLink = false;
  startEditingNewLink(value) {
    this.isEditingNewLink = value;
  }

  projectScreenshot = null;
  async loadProjectScreenshot(project) {
    if (!project) return;

    try {
      const screenshot = await client.getProjectScreenshot(project.id);
      runInAction(() => {
        this.projectScreenshot = screenshot;
      });
    } catch (error) {
      console.error("Ошибка при загрузке скриншота проекта:", error);
    }
  }

  isUpdatingProjectDetails = false;
  async updateProjectDetails(project, { title, link, screenshot }) {
    if (this.isUpdatingProjectDetails) return;
  
    const updateData = {};
    let isValid = true;
  
    if (title !== undefined) {
      if (this.isValidProjectTitle(project.title, title)) {
        updateData.title = title;
      } else {
        isValid = false;
        messageBoxStore.showError("Наименование проекта не заполнено или не отличается от текущего");
      }
    }
  
    if (link !== undefined && link !== project.link) {
      if (validator.isURL(link)) {
        updateData.link = link;
      } else {
        isValid = false;
        messageBoxStore.showError("Недопустимая ссылка");
      }
    }
  
    if (screenshot !== undefined && screenshot !== this.projectScreenshot) {
      updateData.screenshot = screenshot;
    }
  
    if (!isValid || Object.keys(updateData).length === 0) return;
  
    this.isUpdatingProjectDetails = true;
    try {
      await client.updateProject(project.id, updateData);
  
      runInAction(() => {
        if (updateData.title) project.title = updateData.title;
        if (updateData.link) project.link = updateData.link;
        if (updateData.screenshot) project.screenshot = updateData.screenshot;
      });
  
      messageBoxStore.showInfo("Проект успешно обновлен");
      return true;
    } catch (error) {
      messageBoxStore.showError("Ошибка при обновлении проекта. Повторите попытку");
      console.error("Ошибка при обновлении проекта:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isUpdatingProjectDetails = false;
      });
    }
  }

  uploadedFiles = {};
  uploadedFileError = false;
  setUploadedFile(fileType = "default", file) {
    this.uploadedFiles[fileType] = file;
    this.uploadedFileError = false;
    if (fileType === 'default') {
      this.uploadedFile = file;
      this.uploadedFileError = false;
    }
  }

  getUploadedFile(fileType = "default") {
    return this.uploadedFiles[fileType];
  }

  clearUploadedFile(fileType) {
    this.uploadedFiles[fileType] = null;
    this.uploadedFileError = false;
    if (fileType === 'default') {
      this.uploadedFile = null;
      this.uploadedFileError = false;
    }
  }

  computeScreenshotBlob = (file) => {
    return new Promise((res, rej) => {
      const fileReader = new FileReader();
      fileReader.onerror = () => {
        rej(new Error(fileReader.error.message));
      };
      fileReader.onloadend = () => {
        const img = new Image();
        img.onerror = () => {
          rej(new Error('Ошибка преобразования скриншота'));
        };
        img.onload = () => {
          const canvas = document.createElement("canvas");

          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > MAX_SCREENSHOT_SIZE_PX) {
              height *= MAX_SCREENSHOT_SIZE_PX / width;
              width = MAX_SCREENSHOT_SIZE_PX;
            }
          } else {
            if (height > MAX_SCREENSHOT_SIZE_PX) {
              width *= MAX_SCREENSHOT_SIZE_PX / height;
              height = MAX_SCREENSHOT_SIZE_PX;
            }
          }
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            const canvasReader = new FileReader();
            canvasReader.onerror = () => {
              rej(new Error(canvasReader.error.message));
            };
            canvasReader.onloadend = () => {
              const base64data = canvasReader.result.split(',')[1];
              res(base64data);
            };
            canvasReader.readAsDataURL(blob);
          }, "image/jpeg", 1);
        };
        img.src = fileReader.result;
      };
      fileReader.readAsDataURL(file);
    }).catch(() => {
      messageBoxStore.showError('Не удалось обработать скриншот объявления. Пожалуйста, повторите попытку');
      return null;
    });
  }

  showReadyReportsWarning = false;
  setShowReadyReportsWarning(value) {
    this.showReadyReportsWarning = value;
  }

  lastWarningTime = null;
  checkReadyReportsAndShowWarning() {
    const now = new Date().getTime();
    const oneDay = 24 * 60 * 60 * 1000;

    if (this.lastWarningTime === null || now - this.lastWarningTime >= oneDay) {
      const readyReports = this.projects?.flatMap((project) =>
        project.reports.filter((report) => report.status === "ready")
      );

      if (readyReports && readyReports.length > 0) {
        this.setShowReadyReportsWarning(true);
        messageBoxStore.showWarning(
          WARNING_MESSAGE,
          null,
          "Перейти к проверке"
        );
        this.lastWarningTime = now;
      } else {
        this.setShowReadyReportsWarning(false);
      }
    }
  }

  isDropdownLinksOpen = false;
  setIsDropdownLinksOpen(value) {
    this.isDropdownLinksOpen = value;
  }

  searchQuery = "";
  setSearchQuery(query) {
    this.searchQuery = query;
  }

  clearSearchQuery() {
    this.searchQuery = "";
  }

  levenshteinSearch(keyword, query, maxDistance = 1) {
    const lowercaseKeyword = keyword.toLowerCase().trim();
    const lowercaseQuery = query.toLowerCase().trim();

    if (lowercaseKeyword === lowercaseQuery) return true;
    if (lowercaseKeyword.includes(lowercaseQuery)) return true;

    return distance(lowercaseKeyword, lowercaseQuery) <= maxDistance;
  }

  get filteredMarkets() {
    if (!this.searchQuery) return this.marketStores;

    const trimmedQuery = this.searchQuery.trim();
    return this.marketStores.filter(marketStore =>
      marketStore.market?.keywords.some(keyword =>
        this.levenshteinSearch(keyword, trimmedQuery)
      )
    );
  }

  activeTab = "active";
  setActiveTab(tab) {
    if (this.activeTab !== tab) {
      this.reportStatusFilter = null;
      this.showFailedProjects = false;
    }
    this.activeTab = tab;
  }
  
  // filter projects by cities 
  selectedCities = {
    active: {},
    archive: {}
  };
  setSelectedCity(section, type, city) {
    this.selectedCities[section][type] = city;
  }

  getSelectedCity(section, type) {
    return this.selectedCities[section][type] || "Все города";
  }

  getCitiesForType(typeProjects) {
    const citySet = new Set(typeProjects.map(project => project.city || "Без города"));
    const sortedCities = Array.from(citySet)
      .filter(city => city !== "Без города")
      .sort((a, b) => a.localeCompare(b, "ru"));
    return ["Все города", "Без города", ...sortedCities];
  }

  getFilteredProjects(section, type, typeProjects) {
    const selectedCity = this.getSelectedCity(section, type);
    if (selectedCity === "Все города") return typeProjects;
    return typeProjects.filter(project => (project.city || "Без города") === selectedCity);
  }

  rightNowRegistered = false;
  setRightNowRegistered(value = true) {
    this.rightNowRegistered = value;
  }

  // фильтрация проектов по отчетам
  showFailedProjects = false;
  setShowFailedProjects(value) {
    this.showFailedProjects = value;
  }

  activateFailedFilter() {
    this.setActiveTab('active');
    this.setShowFailedProjects(true);
    this.setReportStatusFilter(null);
  }

  reportStatusFilter = null;
  setReportStatusFilter(status) {
    this.setActiveTab("active");
    this.reportStatusFilter = Array.isArray(status) ? status : null;
  }

  resetFailedFilter() {
    this.showFailedProjects = false;
  }

  get reportStatusCounts() {
    const counts = {
      "Все": 0,
      "Ожидают проверки": 0,
      "В работе": 0,
      "В планах": 0
    };
    
    if (!this.projects) return counts;
    
    this.projects.forEach(project => {
      project.reports?.forEach(report => {
        if (report.status === "ready") {
          counts["Ожидают проверки"]++;
        } else if (report.status === "run") {
          counts["В работе"]++;
        } else if (report.status === "new") {
          counts["В планах"]++;
        }
      });
    });
    
    return counts;
  }

  getFilteredActiveProjects() {
    if (!this.projects) return [];
  
    let activeProjects = this.projects.filter(
      project => ["running", "failed"].includes(project.status) && 
      (this.executorType === "humans" ? !project.bots : project.bots)
    );
  
    if (this.showFailedProjects) {
      activeProjects = activeProjects.filter(project => project.status === 'failed');
    }
  
    if (!Array.isArray(this.reportStatusFilter)) return activeProjects;
    
    return activeProjects
      .map(project => {
        const filteredReports = project.reports?.filter(report => 
          this.reportStatusFilter.includes(report.status)
        ) || [];
  
        if (filteredReports.length === 0) return null;
        
        const projectCopy = Object.assign(Object.create(Object.getPrototypeOf(project)), project);
        projectCopy.reports = filteredReports;
        
        return projectCopy;
      })
      .filter(Boolean);
  }
}

const AppStore = new AppStoreClass();
export default AppStore;
